// Tiny i18n: flat keys, {placeholder} interpolation, plurals through
// Intl.PluralRules. Static DOM text is marked up with data-i18n (textContent)
// and data-i18n-aria (aria-label) and re-applied on every language switch.
//
// Each language is a chunk of its own, so a player downloads only theirs: the
// game loads it before it starts (the page preloads the one it'll pick,
// alongside the game: langPreload in vite.config.ts) and on a switch.
// Everything else (link previews, dev pages, tests) has them all at once in
// locales.ts.
//
// Adding a language: copy de.ts, translate, add it to LOADERS, LANG_NAMES and
// LOCALES (locales.ts). The Messages type makes a missing key a compile error.

import type { Message, MessageKey, Messages } from './en';

export type { MessageKey };

/** Each language's texts, as a chunk of its own. */
const LOADERS = {
  en: () => import('./en').then((m): Messages => m.en),
  de: () => import('./de').then((m) => m.de),
};
export type Lang = keyof typeof LOADERS;
export const LANGS = Object.keys(LOADERS) as Lang[];

/** Each language's own name for itself, for the picker. */
export const LANG_NAMES: Record<Lang, string> = { en: 'English', de: 'Deutsch' };

const STORE_KEY = 'kiai-lang';

export type Params = Record<string, string | number>;

let lang: Lang = 'en';
/** The current language's texts, once loaded. */
let messages: Messages | null = null;
let plurals = new Intl.PluralRules('en');
/** The language last asked for: a switch overtaken by another doesn't apply. */
let wanted: Lang = 'en';
const listeners: (() => void)[] = [];

export function isLang(x: unknown): x is Lang {
  return typeof x === 'string' && Object.hasOwn(LOADERS, x);
}

/** ?lang= beats the saved choice beats the browser's languages; English last
 *  (the page's preload picks the same way: vite.config.ts). */
export function detectLang(
  search: string,
  stored: string | null,
  browser: readonly string[],
): Lang {
  const param = new URLSearchParams(search).get('lang');
  if (isLang(param)) return param;
  if (isLang(stored)) return stored;
  for (const tag of browser) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return 'en';
}

export function currentLang(): Lang {
  return lang;
}

/** `msg` in language `l`, its plural picked by `params.count` and its placeholders filled. */
export function format(
  msg: Message,
  l: Lang,
  params: Params = {},
  rules = new Intl.PluralRules(l),
): string {
  const text =
    typeof msg === 'string' ? msg : (msg[rules.select(Number(params.count ?? 0))] ?? msg.other);
  return text.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = params[name];
    return v === undefined ? m : typeof v === 'number' ? fmt(v, l) : v;
  });
}

/** A text in the current language (its key until that's loaded: the game waits for it). */
export function t(key: MessageKey, params?: Params): string {
  return messages ? format(messages[key], lang, params, plurals) : key;
}

export function fmt(n: number, l: Lang = lang): string {
  return n.toLocaleString(l);
}

export function onLangChange(fn: () => void): void {
  listeners.push(fn);
}

/** Load `next` and make it the current language (no DOM: tests use it too);
 *  false if another was asked for meanwhile. */
export async function loadLang(next: Lang): Promise<boolean> {
  wanted = next;
  const m = await LOADERS[next]();
  if (wanted !== next) return false;
  lang = next;
  messages = m;
  plurals = new Intl.PluralRules(next);
  return true;
}

export async function setLang(next: Lang, remember = true): Promise<void> {
  if (remember) {
    try {
      localStorage.setItem(STORE_KEY, next);
    } catch {
      // Storage blocked: the choice lasts for this visit
    }
  }
  if (!(await loadLang(next))) return;
  document.documentElement.lang = next;
  document.title = t('meta.title');
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute('content', t('meta.description'));
  applyDom(document);
  for (const fn of listeners) fn();
}

export async function initLang(): Promise<Lang> {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORE_KEY);
  } catch {
    stored = null;
  }
  const detected = detectLang(location.search, stored, navigator.languages ?? [navigator.language]);
  await setLang(detected, false);
  return detected;
}

function isKey(k: string | undefined): k is MessageKey {
  return k !== undefined && messages !== null && Object.hasOwn(messages, k);
}

export function applyDom(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((node) => {
    const key = node.dataset.i18n;
    if (isKey(key)) node.textContent = t(key);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-aria]').forEach((node) => {
    const key = node.dataset.i18nAria;
    if (isKey(key)) node.setAttribute('aria-label', t(key));
  });
}
